import { useEffect, useState, type ChangeEvent } from 'react';

import { Stack } from '../../../../../components/stack';
import { TextField } from '../../../../../components/text-field';
import { useFenrirTranslation } from '../../../../../locales/use-fenrir-translation';
import type { GridFilterInputValueProps } from '../../../index';

interface InputProps extends GridFilterInputValueProps {
  InputProps?: {
    inputProps: {
      min?: number;
      max?: number;
    };
  };
}

export const GridFilterInputNumericRange = (props: InputProps) => {
  const { t } = useFenrirTranslation({ keyPrefix: 'dataGrid' });
  const { item, applyValue, InputProps } = props;
  const [filterValue, setFilterValue] = useState(item.value ?? ['', '']);

  const min = InputProps?.inputProps.min;
  const max = InputProps?.inputProps.max;

  useEffect(() => {
    setFilterValue(item.value ?? ['', '']);
  }, [item.value]);

  const validateValues = (lowerBound: number, upperBound: number) => {
    if ((lowerBound !== 0 && !lowerBound) || (upperBound !== 0 && !upperBound))
      return false;
    return lowerBound < upperBound;
  };

  const handleLowerFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newLowerBound = event.target.valueAsNumber;
    const upperBound = Number(filterValue[1]);

    if ((min && newLowerBound < min) || (max && newLowerBound > max)) return;

    if (isNaN(newLowerBound)) {
      setFilterValue(['', filterValue[1]]);
    } else {
      setFilterValue([newLowerBound, filterValue[1]]);
    }

    if (validateValues(newLowerBound, upperBound))
      applyValue({ ...item, value: [newLowerBound, upperBound] });
  };

  const handleUpperFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newUpperBound = event.target.valueAsNumber;
    const lowerBound = Number(filterValue[0]);
    if ((min && newUpperBound < min) || (max && newUpperBound > max)) return;

    if (isNaN(newUpperBound)) {
      setFilterValue([filterValue[0], '']);
    } else {
      setFilterValue([filterValue[0], newUpperBound]);
    }

    if (validateValues(lowerBound, newUpperBound))
      applyValue({ ...item, value: [lowerBound, newUpperBound] });
  };

  return (
    <Stack direction="row" spacing={1}>
      <TextField
        name="lower-bound-input"
        placeholder={t('from')}
        label={t('from')}
        value={filterValue[0]}
        onChange={handleLowerFilterChange}
        type="number"
        slotProps={{
          inputLabel: { shrink: true },
        }}
        sx={{ flex: 1 }}
        {...InputProps}
      />
      <TextField
        name="upper-bound-input"
        placeholder={t('to')}
        label={t('to')}
        value={filterValue[1]}
        onChange={handleUpperFilterChange}
        type="number"
        slotProps={{
          inputLabel: { shrink: true },
        }}
        sx={{ flex: 1 }}
        {...InputProps}
      />
    </Stack>
  );
};
